import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Stack, TextField, DatePicker, IconButton } from "@fluentui/react";
import { whereStatementGen } from "../../functions/whereStatementGen";
import {
  buildAdvSearchWhereClause,
  resolveAdvSearchFilterType,
  formatAdvSearchGridDate,
  parseAdvSearchGridDate,
} from "../../functions/advSearchWhereClause";
import { SEARCH_VALUE_ENCODING } from "../../redux/constants/metaData";
import {
  ADVANCED_SEARCH_PLACEHOLDER_NUMBER,
  ADVANCED_SEARCH_PLACEHOLDER_TEXT,
  ADVANCED_SEARCH_TOOLTIP_NUMBER,
  ADVANCED_SEARCH_TOOLTIP_TEXT,
} from "../../redux/constants/messageTypes";
import { useCheckin } from "../../redux/hooks/useCheckin";
import { useTools } from "../../redux/hooks/useTools";
import { mergeStyleSets } from "@fluentui/react/lib/Styling";
import { useTheme } from "../../theme/OAS365Theme";

const AdvSearchObjectFilter = forwardRef(function AdvSearchObjectFilter(
  { showMoreFilters, forceSingleRow = false },
  ref,
) {
  const { advObjConColumns, mruKeyNames } = useCheckin();
  const { onWhereClauseChange } = useTools();
  const theme = useTheme();
  const containerRef = useRef(null);

  const [columns, setColumns] = useState(advObjConColumns);
  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const [itemCols, setItemCols] = useState([]);
  const itemColsRef = useRef(itemCols);
  itemColsRef.current = itemCols;
  /** Display text as typed (capitals preserved); search still case-insensitive in advSearchWhereClause. */
  const [draftValues, setDraftValues] = useState({});
  const draftValuesRef = useRef(draftValues);
  draftValuesRef.current = draftValues;
  const [dateValues, setDateValues] = useState({});
  const dateValuesRef = useRef(dateValues);
  dateValuesRef.current = dateValues;

  useEffect(() => {
    if (Array.isArray(advObjConColumns) && advObjConColumns.length > 0) {
      setColumns(advObjConColumns);
    }
  }, [advObjConColumns]);

  function syncDraftFilters() {
    let next = [...itemColsRef.current];
    const cols = columnsRef.current;
    for (const [fieldId, displayText] of Object.entries(draftValuesRef.current)) {
      const columnId = (fieldId ?? "").trim();
      const col = cols.find((c) => (c.fieldName ?? "").trim() === columnId);
      const filterItem = {
        id: columnId,
        name: (col?.name ?? "").trim(),
        value: encodeSearchValue(displayText ?? ""),
        type: resolveAdvSearchFilterType(col?.dataType, columnId, col?.name),
      };
      next = whereStatementGen(next, filterItem);
    }
    for (const [fieldId, date] of Object.entries(dateValuesRef.current)) {
      if (!date || Number.isNaN(date.getTime?.())) continue;
      const columnId = (fieldId ?? "").trim();
      const col = cols.find((c) => (c.fieldName ?? "").trim() === columnId);
      const filterItem = {
        id: columnId,
        name: (col?.name ?? "").trim(),
        value: formatAdvSearchGridDate(date),
        type: "date",
      };
      next = whereStatementGen(next, filterItem);
    }
    applyFilterItems(next);
  }

  useImperativeHandle(ref, () => ({
    syncDraftFilters,
    getWhereClause: () => {
      syncDraftFilters();
      return buildAdvSearchWhereClause(itemColsRef.current, columnsRef.current);
    },
    getFilterItems: () => {
      syncDraftFilters();
      return [...itemColsRef.current];
    },
    resetFilters: () => {
      setItemCols([]);
      setDraftValues({});
      draftValuesRef.current = {};
      setDateValues({});
      dateValuesRef.current = {};
      itemColsRef.current = [];
      onWhereClauseChange("");
    },
  }));

  const classNames = mergeStyleSets({
    wrapper: {
      position: "relative",
      overflowX: "hidden",
    },
    gridContainer: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
      padding: "5px",
      "@media (max-width: 1366px)": {
        gridTemplateColumns: "repeat(3, 1fr)",
      },
      "@media (max-width: 1024px)": {
        gridTemplateColumns: "repeat(2, 1fr)",
      },
      "@media (max-width: 768px)": {
        gridTemplateColumns: "1fr",
      },
    },
    filterItem: {
      backgroundColor: theme.colors.backgroundColor,
      padding: "10px",
      borderRadius: "4px",
      display: "flex",
      flexDirection: "column",
      gap: "1px",
    },
    label: {
      fontFamily: theme.fonts.regular,
      fontSize: theme.fontSizes.normal,
      color: theme.colors.fontColor,
      marginBottom: "8px",
      height: "20px",
      overflow: "hidden",
      display: "block",
      width: "100%",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
    },
    dateField: {
      selectors: {
        ".ms-TextField-fieldGroup": {
          position: "relative",
        },
        ".ms-TextField-suffix": {
          display: "flex",
          alignItems: "center",
          position: "absolute",
          right: 30,
          top: 0,
          bottom: 0,
          zIndex: 1,
        },
        ".ms-TextField-icon": {
          position: "absolute",
          right: 8,
          top: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          marginLeft: 0,
          zIndex: 2,
        },
      },
    },
  });

  const outerStackTokens = { childrenGap: showMoreFilters ? 0 : 5 };

  const mainFieldsCount = Math.min(columns.length, mruKeyNames.length);
  const itemsToShow = showMoreFilters ? columns.length : mainFieldsCount;
  const singleRowTemplate = `repeat(${Math.max(1, mainFieldsCount)}, minmax(160px, 1fr))`;
  const expandedTemplate = "repeat(auto-fill, minmax(140px, 220px))";

  const renderLabel = (name) => (
    <div className={classNames.label}>
      {name}
    </div>
  );

  const getFieldValue = (fieldName) => {
    const key = (fieldName ?? "").trim();
    if (Object.prototype.hasOwnProperty.call(draftValues, key)) {
      return draftValues[key];
    }
    const item = itemCols.find((col) => col.id === key);
    return item?.value ?? "";
  };

  const getDateFieldValue = (fieldName) => {
    const key = (fieldName ?? "").trim();
    if (Object.prototype.hasOwnProperty.call(dateValues, key)) {
      return dateValues[key];
    }
    const item = itemCols.find((col) => col.id === key);
    return item?.value ? parseAdvSearchGridDate(item.value) : undefined;
  };

  const clearSuffixFieldStyles = {
    suffix: {
      backgroundColor: "transparent",
      background: "none",
      padding: 0,
      margin: 0,
    },
  };

  const clearIconButtonStyles = {
    root: {
      height: 24,
      width: 24,
      backgroundColor: "transparent",
      background: "none",
    },
    rootHovered: {
      backgroundColor: "transparent",
      background: "none",
    },
    rootPressed: {
      backgroundColor: "transparent",
      background: "none",
    },
    icon: {
      fontSize: 12,
      color: theme.colors.addovation70Green,
    },
    iconHovered: {
      color: theme.colors.addovation90Green,
    },
  };

  const renderClearSuffix = (hasValue, onClear) => {
    if (!hasValue) return null;
    return (
      <IconButton
        iconProps={{
          iconName: "Cancel",
          styles: { root: { color: theme.colors.addovation70Green } },
        }}
        title="Clear"
        ariaLabel="Clear"
        styles={clearIconButtonStyles}
        onClick={(ev) => {
          ev?.preventDefault?.();
          ev?.stopPropagation?.();
          onClear();
        }}
      />
    );
  };

  const renderDateClearSuffix = (hasValue, onClear) => {
    if (!hasValue) return null;
    return (
      <IconButton
        iconProps={{
          iconName: "Cancel",
          styles: { root: { color: theme.colors.addovation70Green } },
        }}
        title="Clear"
        ariaLabel="Clear"
        styles={clearIconButtonStyles}
        onClick={(ev) => {
          ev?.preventDefault?.();
          ev?.stopPropagation?.();
          onClear();
        }}
      />
    );
  };

  const clearTextField = (fieldName, dataType, columnLabel) => {
    onChange("", fieldName, dataType, columnLabel);
  };

  const renderField = (item) => {
    const fieldName = (item.fieldName ?? "").trim();
    const textValue = getFieldValue(fieldName);
    const commonTextFieldProps = {
      id: fieldName,
      value: textValue,
      disabled: false,
      readOnly: false,
      styles: {
        root: {
          width: "100%",
          fontFamily: theme.fonts.regular,
          fontSize: theme.fontSizes.normal,
          backgroundColor: theme.colors.backgroundColor,
        },
        fieldGroup: {
          backgroundColor: theme.colors.backgroundColor,
          borderColor: theme.colors.Addovation20Grey,
          selectors: {
            ".ms-TextField-suffix": {
              backgroundColor: "transparent",
              background: "none",
            },
          },
        },
        field: {
          color: theme.colors.fontColor,
          backgroundColor: theme.colors.backgroundColor,
        },
        ...clearSuffixFieldStyles,
      },
      onChange: (ev, text) =>
        onChange(text ?? ev?.target?.value ?? "", fieldName, item.dataType, item.name),
      onRenderSuffix: () =>
        renderClearSuffix(!!textValue, () =>
          clearTextField(fieldName, item.dataType, item.name),
        ),
    };

    if (item.dataType.toUpperCase().includes("STRING")) {
      return (
        <div className={classNames.filterItem} key={item.key}>
          {renderLabel(item.name)}
          <TextField
            {...commonTextFieldProps}
            placeholder={ADVANCED_SEARCH_PLACEHOLDER_TEXT}
            onRenderInput={(props, defaultRender) =>
              defaultRender?.({ ...props, title: ADVANCED_SEARCH_TOOLTIP_TEXT })
            }
          />
        </div>
      );
    }
    if (item.dataType.toUpperCase().includes("NUMBER")) {
      return (
        <div className={classNames.filterItem} key={item.key}>
          {renderLabel(item.name)}
          <TextField
            {...commonTextFieldProps}
            placeholder={ADVANCED_SEARCH_PLACEHOLDER_NUMBER}
            onRenderInput={(props, defaultRender) =>
              defaultRender?.({ ...props, title: ADVANCED_SEARCH_TOOLTIP_NUMBER })
            }
          />
        </div>
      );
    }
    if (item.dataType.toUpperCase().includes("DATE")) {
      const dateValue = getDateFieldValue(fieldName);
      const hasDateValue =
        dateValue instanceof Date && !Number.isNaN(dateValue.getTime?.());
      return (
        <div
          className={`${classNames.filterItem} ${classNames.dateField}`}
          key={item.key}
        >
          {renderLabel(item.name)}
          <DatePicker
            id={fieldName}
            value={dateValue}
            formatDate={formatAdvSearchGridDate}
            parseDateFromString={parseAdvSearchGridDate}
            styles={{
              root: {
                width: "100%",
                fontFamily: theme.fonts.regular,
                fontSize: theme.fontSizes.normal,
              },
              textField: {
                ...clearSuffixFieldStyles,
                field: {
                  paddingRight: hasDateValue ? 52 : 28,
                },
                selectors: {
                  ".ms-TextField-fieldGroup": {
                    position: "relative",
                    backgroundColor: theme.colors.backgroundColor,
                    borderColor: theme.colors.Addovation20Grey,
                  },
                  ".ms-TextField-field": {
                    color: theme.colors.fontColor,
                    backgroundColor: theme.colors.backgroundColor,
                  },
                  ".ms-TextField-suffix": {
                    backgroundColor: "transparent",
                    background: "none",
                    position: "absolute",
                    right: 30,
                    top: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    zIndex: 1,
                  },
                  ".ms-TextField-icon": {
                    position: "absolute",
                    right: 8,
                    top: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    height: "auto",
                    lineHeight: "normal",
                    zIndex: 2,
                  },
                },
              },
              icon: {
                color: theme.colors.addovation70Green,
                position: "absolute",
                right: 8,
                top: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                height: "auto",
                lineHeight: "normal",
              },
            }}
            textField={{
              onRenderSuffix: () =>
                renderDateClearSuffix(hasDateValue, () =>
                  handleDateSelection(fieldName, null),
                ),
            }}
            allowTextInput
            showWeekNumbers={true}
            firstWeekOfYear={1}
            placeholder="YYYY-MM-DD"
            ariaLabel="Select a date"
            onSelectDate={(date) => handleDateSelection(fieldName, date)}
          />
        </div>
      );
    }

    return (
      <div className={classNames.filterItem} key={item.key}>
        {renderLabel(item.name)}
        <TextField
          {...commonTextFieldProps}
          placeholder={ADVANCED_SEARCH_PLACEHOLDER_TEXT}
          onRenderInput={(props, defaultRender) =>
            defaultRender?.({ ...props, title: ADVANCED_SEARCH_TOOLTIP_TEXT })
          }
        />
      </div>
    );
  };

  return (
    <div className="oas-adv-search">
      <Stack tokens={outerStackTokens} className={classNames.wrapper}>
        {showMoreFilters ? (
          <>
            <div
              ref={containerRef}
              className={classNames.gridContainer}
              style={{
                gridTemplateColumns: expandedTemplate,
                width: "100%",
                justifyContent: "start",
                paddingTop: "0px",
                paddingBottom: "0px",
              }}
            >
              {columns.slice(0, mainFieldsCount).map(renderField)}
            </div>
            <div
              className={classNames.gridContainer}
              style={{
                gridTemplateColumns: expandedTemplate,
                width: "100%",
                justifyContent: "start",
                paddingTop: "0px",
                paddingBottom: "0px",
              }}
            >
              {columns.slice(mainFieldsCount).map(renderField)}
            </div>
          </>
        ) : (
          <div
            ref={containerRef}
            className={classNames.gridContainer}
            style={
              forceSingleRow
                ? {
                    gridTemplateColumns: singleRowTemplate,
                    width: "100%",
                  }
                : undefined
            }
          >
            {columns.slice(0, itemsToShow).map(renderField)}
          </div>
        )}
      </Stack>
    </div>
  );

  function encodeSearchValue(value) {
    if (!value) return value;

    let encodedValue = value;

    if (encodedValue.includes(SEARCH_VALUE_ENCODING.HASH_SYMBOL)) {
      encodedValue = encodedValue.replace(
        new RegExp(SEARCH_VALUE_ENCODING.HASH_SYMBOL, "g"),
        SEARCH_VALUE_ENCODING.HASH_ENCODED,
      );
    }

    if (encodedValue.includes(SEARCH_VALUE_ENCODING.AMPERSAND_SYMBOL)) {
      encodedValue = encodedValue.replace(
        new RegExp(SEARCH_VALUE_ENCODING.AMPERSAND_SYMBOL, "g"),
        SEARCH_VALUE_ENCODING.AMPERSAND_ENCODED,
      );
    }

    return encodedValue;
  }

  function applyFilterItems(nextItems) {
    setItemCols(nextItems);
    itemColsRef.current = nextItems;
    // Do not dispatch whereClause on every keystroke — it re-renders the results
    // grid and steals focus from these inputs.
  }

  function onChange(text, fieldName, dataType, columnLabel) {
    const columnId = (fieldName ?? "").trim();
    const displayText = text ?? "";
    setDraftValues((prev) => ({ ...prev, [columnId]: displayText }));

    const encodedText = encodeSearchValue(displayText);
    const filterItem = {
      id: columnId,
      name: (columnLabel ?? "").trim(),
      value: encodedText,
      type: resolveAdvSearchFilterType(dataType, columnId, columnLabel),
    };

    applyFilterItems(whereStatementGen(itemColsRef.current, filterItem));
  }

  function handleDateSelection(fieldName, date) {
    const columnId = (fieldName ?? "").trim();
    setDateValues((prev) => {
      const next = { ...prev };
      if (!date || Number.isNaN(date.getTime?.())) {
        delete next[columnId];
      } else {
        next[columnId] = date;
      }
      return next;
    });

    let filterItem;

    if (date && !Number.isNaN(date.getTime?.())) {
      const col = columns.find((c) => (c.fieldName ?? "").trim() === columnId);
      filterItem = {
        id: columnId,
        name: (col?.name ?? "").trim(),
        value: formatAdvSearchGridDate(date),
        type: "date",
      };
    } else {
      filterItem = {
        id: columnId,
        value: "",
        type: "date",
      };
    }

    applyFilterItems(whereStatementGen(itemColsRef.current, filterItem));
  }
});

export default React.memo(AdvSearchObjectFilter);
